import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNotifications } from '@fullstory/react-hooks';
import { AppDispatch } from '../../types';
import { getOrgId } from '../fullstory/selectors';
import { useCallback } from 'react';
import { ElementPropertyRulesRequestActions } from './actions';
import { ElementPropertyRule } from '@fullstory/proto/fs/services/search-settings';

export const useElementPropertyRuleActions = () => {
  const intl = useIntl();
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  const save = useCallback(
    async (elementPropertyRule: ElementPropertyRule) => {
      try {
        const response = await dispatch(
          ElementPropertyRulesRequestActions.setElementPropertyRule(orgId, elementPropertyRule),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Saved Element Property Rule',
            id: '+dDsNR',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to save Element Property Rule',
            id: 'T3MqNd',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const archive = useCallback(
    async (elementPropertyRuleId: string) => {
      try {
        const response = await dispatch(
          ElementPropertyRulesRequestActions.archiveElementPropertyRule(
            orgId,
            elementPropertyRuleId,
          ),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Archived Element Property Rule',
            id: 'iZ0P/q',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive Element Property Rule',
            id: 'aMzyTh',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const restore = useCallback(
    async (elementPropertyRuleId: string) => {
      try {
        const response = await dispatch(
          ElementPropertyRulesRequestActions.unarchiveElementPropertyRule(
            orgId,
            elementPropertyRuleId,
          ),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Restored Element Property Rule',
            id: 'wWg1LY',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore Element Property Rule',
            id: 'UdVeGv',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const archiveMulti = useCallback(
    async (elementPropertyRuleIds: string[]) => {
      try {
        const response = await dispatch(
          ElementPropertyRulesRequestActions.archiveMultiElementPropertyRule(
            orgId,
            elementPropertyRuleIds,
          ),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Archived Element Property Rules',
            id: '79R6Ad',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive Element Property Rules',
            id: 'osCqS+',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  const restoreMulti = useCallback(
    async (elementPropertyRuleIds: string[]) => {
      try {
        const response = await dispatch(
          ElementPropertyRulesRequestActions.unarchiveMultiElementPropertyRule(
            orgId,
            elementPropertyRuleIds,
          ),
        );
        notifications.success(
          intl.formatMessage({
            defaultMessage: 'Restored Element Property Rules',
            id: 'eqjlVg',
          }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore Element Property Rules',
            id: 'NwkCiK',
          }),
        );
        return null;
      }
    },
    [dispatch, orgId, notifications, intl],
  );

  return {
    save,
    archive,
    restore,
    archiveMulti,
    restoreMulti,
  };
};
